import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LAST_ACTIVITY_KEY = 'meuFiel_lastActivity';

// Hook para controle de sessão com timeout de inatividade
export const useSessionTimeout = (timeoutMinutes = 30) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  const TIMEOUT_DURATION = timeoutMinutes * 60 * 1000; // Converter para millisegundos
  const WARNING_TIME = 5 * 60 * 1000; // Aviso 5 minutos antes
  const isActive = user && timeoutMinutes > 0; // Determinar se deve estar ativo

  // Função para fazer logout por inatividade
  const handleTimeout = useCallback(async () => {
    try {
      // Limpar localStorage
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      
      await logout();
      // Redirecionar para login com mensagem
      navigate('/login?timeout=true', { 
        replace: true,
        state: { message: 'Sessão expirou por inatividade' }
      });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Forçar redirecionamento mesmo com erro
      navigate('/login?timeout=true', { replace: true });
    }
  }, [logout, navigate]);

  // Função para mostrar aviso de timeout
  const showTimeoutWarning = useCallback(() => {
    const confirmed = window.confirm(
      'Sua sessão expirará em 5 minutos devido à inatividade. Clique em OK para continuar usando o sistema.'
    );
    
    if (confirmed) {
      // Usuário confirmou, usar ref para resetar timer mais tarde
      // Esta função será definida após showTimeoutWarning
      return 'continue';
    } else {
      // Usuário não confirmou, fazer logout imediatamente
      handleTimeout();
      return 'logout';
    }
  }, [handleTimeout]);

  // Salvar atividade no localStorage
  const saveActivity = useCallback((timestamp) => {
    if (isActive) {
      localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString());
    }
  }, [isActive]);

  // Função para resetar o timer de inatividade
  const resetTimer = useCallback(() => {
    if (!isActive) return;
    
    const now = Date.now();
    lastActivityRef.current = now;
    saveActivity(now);
    
    // Limpar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Timer para aviso (timeout - 5 minutos)
    warningTimeoutRef.current = setTimeout(() => {
      const result = showTimeoutWarning();
      if (result === 'continue') {
        // Usuário confirmou, resetar timer novamente
        resetTimer();
      }
      // Se result === 'logout', handleTimeout já foi chamado em showTimeoutWarning
    }, TIMEOUT_DURATION - WARNING_TIME);
    
    // Timer para logout automático
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, TIMEOUT_DURATION);
  }, [isActive, TIMEOUT_DURATION, WARNING_TIME, showTimeoutWarning, handleTimeout, saveActivity]);

  // Eventos que resetam o timer (atividade do usuário)
  const handleUserActivity = useCallback(() => {
    // Throttle: só resetar se passou mais de 1 minuto desde a última atividade
    const now = Date.now();
    if (now - lastActivityRef.current > 60000) { // 1 minuto
      resetTimer();
    }
  }, [resetTimer]);

  // Carregar última atividade do localStorage
  useEffect(() => {
    if (!isActive) return;
    
    const savedActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (savedActivity) {
      const timestamp = parseInt(savedActivity);
      const now = Date.now();
      
      // Se passou do timeout, fazer logout imediatamente
      if (now - timestamp > TIMEOUT_DURATION) {
        handleTimeout();
        return;
      }
      
      lastActivityRef.current = timestamp;
    }
  }, [isActive, TIMEOUT_DURATION, handleTimeout]);

  // Configurar listeners de eventos
  useEffect(() => {
    if (!isActive) return; // Não configurar se não estiver ativo

    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Adicionar listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Iniciar timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [isActive, handleUserActivity, resetTimer]);

  // Cleanup quando componente desmonta
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  return {
    resetTimer: isActive ? resetTimer : () => {}, // Função para resetar manualmente o timer
    timeoutMinutes: isActive ? timeoutMinutes : 0 // Retorna o tempo configurado
  };
};
